import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Grid, Typography } from "@mui/material";
import moment from "moment";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import CustomButton from "../../../components/CustomButton";
import CustomModal from "../../../components/CustomModal/CustomModal";
import CustomTextField from "../../../components/CustomTextField";
import CustomUpload from "../../../components/CustomUpload";
import CustomSelect from "../../../components/Select/Select";
import { showSnackBar } from "../../../redux/reducers/snackSlice";
import {
  closeSupportTicket,
  createSupportTicket,
  getSupportTickets,
  replySupportTicket,
} from "../../../redux/reducers/supportSlice";
import FilesWithCloseButton from "../../Hospital/ClinicPetDetails/PatientWorkbook/FilesWithCloseButton";
import "./Support.css";
import {
  SUPPORT_CATEGORIES,
  SUPPORT_PROBLEM_TYPES,
  SUPPORT_REASONS_BY_CATEGORY,
  toSelectList,
} from "./supportData";

const initForm = {
  category: "",
  reason: "",
  problemType: "",
  description: "",
  attachments: [],
};

const initErrors = {
  category: "",
  reason: "",
  problemType: "",
  description: "",
  file: "",
};

const toFilesArray = (value) => {
  if (value == null || value === "") return [];
  if (Array.isArray(value)) return value;
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) return parsed;
      if (parsed) return [parsed];
    } catch {
      return [value];
    }
  }
  return [value];
};

const mapFileItem = (item, fallbackName = "file") => {
  if (typeof item === "string") {
    return { previewUrl: item, fileName: fallbackName };
  }
  return {
    previewUrl:
      item?.previewUrl ||
      item?.url ||
      item?.fileUrl ||
      item?.filePath ||
      item?.path ||
      "",
    fileName:
      item?.fileName || item?.name || item?.originalName || fallbackName,
    file: item?.file,
  };
};

const normalizeFileList = (value, fallbackName = "file") =>
  toFilesArray(value)
    .map((item) => mapFileItem(item, fallbackName))
    .filter((f) => f.previewUrl);

const normalizeTicket = (ticket, index = 0) => {
  // Attachment and files are separate arrays from API
  const attachments = normalizeFileList(
    ticket?.attachment ?? ticket?.attachments,
    "attachment",
  );
  const files = normalizeFileList(ticket?.files, "file");
  const replies = (Array.isArray(ticket?.replies) ? ticket.replies : []).map(
    (reply, replyIndex) => ({
      replyId: reply?.replyId || reply?.id || `reply_${replyIndex}`,
      message: reply?.message ?? "",
      attachments: normalizeFileList(
        reply?.attachment ?? reply?.attachments,
        "attachment",
      ),
      createdBy: reply?.createdBy,
      createdByName: reply?.createdByName || "",
      createdDate: reply?.createdDate || reply?.createdAt || null,
    }),
  );

  return {
    id: ticket?.id || ticket?.ticketId || `ticket_${index}`,
    ticketId: ticket?.ticketId || ticket?.id || `ticket_${index}`,
    ticketName: ticket?.ticketName || ticket?.name || "",
    ticketNumber: ticket?.ticketNumber || "",
    category: ticket?.mainCategory || ticket?.category || "",
    mainCategory: ticket?.mainCategory || ticket?.category || "",
    reason: ticket?.reason || "",
    problemType: ticket?.problem || ticket?.problemType || "",
    description: ticket?.description || "",
    remark: ticket?.remark || ticket?.remarks || "",
    attachments,
    files,
    replies,
    status: ticket?.status || "Open",
    createdAt:
      ticket?.createdAt || ticket?.createdDate || ticket?.createdOn || null,
  };
};

const SupportTicketDetailView = ({
  ticket,
  onBack,
  onReplySuccess,
  onCloseSuccess,
}) => {
  const dispatch = useDispatch();
  const user = JSON.parse(localStorage.getItem("user") || "null");
  const [replyMessage, setReplyMessage] = useState("");
  const [replyAttachments, setReplyAttachments] = useState([]);
  const [replyError, setReplyError] = useState("");
  const [replyModalOpen, setReplyModalOpen] = useState(false);
  const [closeConfirmOpen, setCloseConfirmOpen] = useState(false);
  const chatThreadRef = useRef(null);

  const resetReplyForm = () => {
    setReplyMessage("");
    setReplyAttachments([]);
    setReplyError("");
  };

  useEffect(() => {
    resetReplyForm();
    setReplyModalOpen(false);
  }, [ticket?.ticketId]);

  useEffect(() => {
    if (chatThreadRef.current) {
      chatThreadRef.current.scrollTop = chatThreadRef.current.scrollHeight;
    }
  }, [ticket?.replies?.length, ticket?.ticketId]);

  if (!ticket) return null;

  const statusLabel = String(ticket?.status ?? "Open");
  const isResolved = statusLabel.toLowerCase() === "resolved";
  const isOpen = statusLabel.toLowerCase() === "open";
  const attachments = Array.isArray(ticket?.attachments)
    ? ticket.attachments
    : [];
  const files = Array.isArray(ticket?.files) ? ticket.files : [];
  const replies = Array.isArray(ticket?.replies) ? ticket.replies : [];
  const canReply =
    !isResolved && (Number(user?.id) === 1 || replies.length >= 2);

  const isMine = (createdBy) =>
    createdBy != null && Number(createdBy) === Number(user?.id);

  const handleRemoveReplyAttachment = (index) => {
    setReplyAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const onUploadReplyAttachment = (e) => {
    const fileList = e?.target?.files;
    if (!fileList?.length) return;

    const selectedFiles = Array.from(fileList);
    const invalidFile = selectedFiles.find(
      (file) => !file.type?.startsWith("image/"),
    );
    if (invalidFile) {
      setReplyError("Only image files are allowed");
      dispatch(
        showSnackBar({
          message: "Only image files are allowed",
          type: "error",
        }),
      );
      e.target.value = "";
      return;
    }

    const readFiles = selectedFiles.map(
      (file) =>
        new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            resolve({
              file,
              previewUrl: reader.result,
              fileName: file.name,
            });
          };
          reader.onerror = () => reject(reader.error);
          reader.readAsDataURL(file);
        }),
    );

    Promise.all(readFiles)
      .then((newAttachments) => {
        setReplyAttachments((prev) => [...prev, ...newAttachments]);
        setReplyError("");
      })
      .catch(() => {
        dispatch(
          showSnackBar({
            message: "Unable to read selected images",
            type: "error",
          }),
        );
      })
      .finally(() => {
        e.target.value = "";
      });
  };

  const handleSendReply = async () => {
    if (!replyMessage.trim()) {
      setReplyError("Message is required");
      return;
    }

    const formData = new FormData();
    formData.append("message", replyMessage);
    (replyAttachments ?? []).forEach((item) => {
      if (item?.file) {
        formData.append("attachment", item.file);
      }
    });

    const res = await dispatch(
      replySupportTicket({
        ticketId: ticket?.ticketId || ticket?.id,
        form: formData,
      }),
    );

    if (res?.payload) {
      resetReplyForm();
      setReplyModalOpen(false);
      onReplySuccess?.(ticket?.ticketId || ticket?.id, res.payload);
    }
  };

  const handleCloseReplyModal = () => {
    setReplyModalOpen(false);
    resetReplyForm();
  };

  const handleConfirmClose = async () => {
    const ticketId = ticket?.ticketId || ticket?.id;
    const form = new FormData();
    form.append("status", "Resolved");
    const res = await dispatch(closeSupportTicket({ ticketId, form }));
    if (res?.payload) {
      setCloseConfirmOpen(false);
      onCloseSuccess?.(ticketId, res.payload);
    }
  };

  const metaItems = [
    { label: "Ticket Name", value: ticket?.ticketName || "-" },
    {
      label: "Category",
      value: ticket?.mainCategory || ticket?.category || "-",
    },
    { label: "Reason", value: ticket?.reason || "-" },
    { label: "Problem Type", value: ticket?.problemType || "-" },
    {
      label: "Created On",
      value: ticket?.createdAt
        ? moment(ticket.createdAt).format("DD MMM YYYY, hh:mm A")
        : "-",
    },
  ];

  return (
    <>
      <div className="support-page-header">
        <div className="support-page-header-left">
          <button
            type="button"
            className="support-back-arrow"
            onClick={onBack}
            aria-label="Back"
          >
            <ArrowBackIcon sx={{ width: 22, height: 22 }} />
          </button>
          <Typography className="font-bold fs22 black">
            Support Ticket{/* - {ticket?.ticketNumber ?? ""} */}
          </Typography>
          <span
            className={`support-status-pill ${
              isOpen ? "support-status-active" : "support-status-resolved"
            }`}
          >
            {statusLabel.toUpperCase()}
          </span>
        </div>
        <div className="flex-row" style={{ gap: 10 }}>
          {canReply ? (
            <div>
              <CustomButton
                text="Reply"
                smallBtn
                normalCase
                onClick={() => setReplyModalOpen(true)}
              />
            </div>
          ) : null}
          {!isResolved && Number(user?.id) === 1 ? (
            <div className="ml10">
              <CustomButton
                text="Mark as Closed"
                smallBtn
                normalCase
                onClick={() => setCloseConfirmOpen(true)}
              />
            </div>
          ) : null}
        </div>
      </div>

      <CustomModal
        open={replyModalOpen}
        onClose={handleCloseReplyModal}
        header="Reply"
        modal
        modalWidth={80}
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography className="support-field-label">MESSAGE</Typography>
            <CustomTextField
              fullWidth
              multiline
              rows={4}
              placeholder="Type a reply..."
              value={replyMessage}
              handleChange={(e) => {
                setReplyMessage(e?.target?.value);
                if (replyError) setReplyError("");
              }}
              error={!!replyError}
              helperText={replyError}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography className="support-field-label">
              ATTACHMENT (IMAGES ONLY)
            </Typography>
            {replyAttachments.length > 0 ? (
              <FilesWithCloseButton
                files={replyAttachments}
                onClickFile={handleRemoveReplyAttachment}
              />
            ) : null}
            <CustomUpload
              onUploadFile={onUploadReplyAttachment}
              multipleNew
              multiFiles
              fileAccept="image/*"
            />
          </Grid>
        </Grid>
        <div className="flex-center mb10 mt15">
          {/* <div>
            <CustomButton
              text="Cancel"
              onClick={handleCloseReplyModal}
              smallBtn
              redBtn
            />
          </div> */}
          <div>
            <CustomButton text="Submit" onClick={handleSendReply} smallBtn />
          </div>
        </div>
      </CustomModal>

      <CustomModal
        open={closeConfirmOpen}
        onClose={() => setCloseConfirmOpen(false)}
        header="Close Ticket"
        modal
        modalWidth={30}
      >
        <Typography className="flex-center txt-regular fs14">
          Are you sure you want to mark this ticket as closed?
        </Typography>
        <div className="flex-center mb10 mt15">
          <div>
            <CustomButton
              text="Cancel"
              onClick={() => setCloseConfirmOpen(false)}
              smallBtn
              redBtn
            />
          </div>
          <div className="ml10">
            <CustomButton
              text="Confirm"
              onClick={handleConfirmClose}
              smallBtn
            />
          </div>
        </div>
      </CustomModal>

      <div className="support-chat-shell">
        <div className="support-chat-top">
          <div className="support-chat-meta">
            {metaItems.map((item) => (
              <div key={item.label} className="support-chat-meta-item">
                <span className="support-chat-meta-label">{item.label}</span>
                <span className="support-chat-meta-value">{item.value}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="support-chat-thread" ref={chatThreadRef}>
          {isResolved && (ticket?.remark || files.length > 0) ? (
            <div className="support-chat-row support-chat-row-other">
              <div className="support-chat-bubble support-chat-bubble-other">
                <div className="support-chat-bubble-meta">
                  <span className="support-chat-bubble-author">Remark</span>
                </div>
                {ticket?.remark ? (
                  <div className="support-chat-bubble-text">
                    {ticket.remark}
                  </div>
                ) : null}
                {files.length > 0 ? (
                  <div className="support-chat-attachments">
                    <FilesWithCloseButton files={files} disabled />
                  </div>
                ) : null}
              </div>
            </div>
          ) : null}

          {replies.length === 0 ? (
            <div className="support-chat-empty">No replies yet</div>
          ) : (
            replies.map((reply) => {
              const mine = isMine(reply?.createdBy);
              return (
                <div
                  key={reply?.replyId}
                  className={`support-chat-row ${
                    mine ? "support-chat-row-mine" : "support-chat-row-other"
                  }`}
                >
                  <div
                    className={`support-chat-bubble ${
                      mine
                        ? "support-chat-bubble-mine"
                        : "support-chat-bubble-other"
                    }`}
                  >
                    <div className="support-chat-bubble-meta">
                      <span className="support-chat-bubble-author">
                        {reply?.createdByName || (mine ? "You" : "User")}
                      </span>
                      <span className="support-chat-bubble-date">
                        {reply?.createdDate
                          ? moment(reply.createdDate).format(
                              "DD MMM YYYY, hh:mm A",
                            )
                          : "-"}
                      </span>
                    </div>
                    <div
                      className="support-chat-bubble-text"
                      style={{ whiteSpace: "pre-wrap", textTransform: "none" }}
                    >
                      {reply?.message ?? "-"}
                    </div>
                    {reply?.attachments?.length > 0 ? (
                      <div className="support-chat-attachments">
                        <FilesWithCloseButton
                          files={reply.attachments}
                          disabled
                        />
                      </div>
                    ) : null}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </>
  );
};

const SupportTicketCard = ({ ticket, onView }) => {
  const user = JSON.parse(localStorage.getItem("user") || "null");
  const statusLabel = String(ticket?.status ?? "Open");
  const isOpen = statusLabel.toLowerCase() === "open";

  const showResponseReceived = (() => {
    if (!isOpen) return false;
    const replies = Array.isArray(ticket?.replies) ? ticket.replies : [];
    if (replies.length <= 1) return false;
    const latest = [...replies].sort(
      (a, b) => Number(b?.replyId ?? 0) - Number(a?.replyId ?? 0),
    )?.[0];
    if (latest?.createdBy == null || user?.id == null) return false;
    return Number(latest.createdBy) !== Number(user.id);
  })();

  return (
    <div className="support-card">
      <div className="support-card-main">
        <div className="support-card-title-row">
          <div className="support-card-title">
            {ticket?.mainCategory || ticket?.category || "-"}
          </div>
          {ticket?.ticketName ? (
            <div className="support-card-ticket-no">{ticket.ticketName}</div>
          ) : null}
          {/* {ticket?.ticketNumber ? (
            <div className="support-card-ticket-no">{ticket.ticketNumber}</div>
          ) : null} */}
          <span
            className={`support-status-pill ${
              isOpen ? "support-status-active" : "support-status-resolved"
            }`}
          >
            {isOpen ? "OPEN" : "CLOSED"}
          </span>
        </div>
        <div className="support-card-remarks support-card-remark-text">
          {ticket?.description || "-"}
        </div>
        <div className="support-card-meta">
          {ticket.createdAt
            ? moment(ticket.createdAt).format("DD MMM YYYY, hh:mm A")
            : "-"}
        </div>
      </div>
      <div className="support-card-actions">
        {showResponseReceived ? (
          <span className="support-status-pill support-status-response">
            RESPONSE RECEIVED
          </span>
        ) : null}
        <div className="support-card-view-btn">
          <CustomButton text="View" smallBtn onClick={() => onView(ticket)} />
        </div>
      </div>
    </div>
  );
};

const Support = () => {
  const dispatch = useDispatch();
  const user = JSON.parse(localStorage.getItem("user"));
  const [view, setView] = useState("list");
  const [tickets, setTickets] = useState([]);
  const [form, setForm] = useState(initForm);
  const [errors, setErrors] = useState(initErrors);
  const [viewTicket, setViewTicket] = useState(null);

  const fetchTickets = (keepTicketId) => {
    dispatch(getSupportTickets()).then((res) => {
      const payload = res?.payload;
      if (!payload) {
        setTickets([]);
        return;
      }
      const list = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.tickets)
          ? payload.tickets
          : Array.isArray(payload?.content)
            ? payload.content
            : [];
      const normalized = list.map((ticket, index) =>
        normalizeTicket(ticket, index),
      );
      setTickets(normalized);

      if (keepTicketId) {
        const updated = normalized.find(
          (t) =>
            String(t?.ticketId) === String(keepTicketId) ||
            String(t?.id) === String(keepTicketId),
        );
        if (updated) setViewTicket(updated);
      }
    });
  };

  const handleReplySuccess = (ticketId, payload) => {
    if (payload && typeof payload === "object" && !Array.isArray(payload)) {
      const updated = normalizeTicket(payload);
      setViewTicket(updated);
      setTickets((prev) =>
        prev.map((t) =>
          String(t?.ticketId) === String(updated.ticketId) ||
          String(t?.id) === String(updated.id)
            ? updated
            : t,
        ),
      );
      return;
    }
    fetchTickets(ticketId);
  };

  const handleCloseSuccess = (ticketId, payload) => {
    if (payload && typeof payload === "object" && !Array.isArray(payload)) {
      const updated = normalizeTicket({ ...payload, status: "Resolved" });
      setViewTicket(updated);
      setTickets((prev) =>
        prev.map((t) =>
          String(t?.ticketId) === String(updated.ticketId) ||
          String(t?.id) === String(updated.id)
            ? updated
            : t,
        ),
      );
    } else {
      setViewTicket((prev) =>
        prev
          ? {
              ...prev,
              status: "Resolved",
            }
          : prev,
      );
    }
    fetchTickets(ticketId);
  };

  useEffect(() => {
    fetchTickets();
  }, []);

  const activeTickets = useMemo(
    () =>
      (tickets ?? []).filter(
        (t) => (t?.status ?? "")?.toLowerCase() === "open",
      ),
    [tickets],
  );

  const resolvedTickets = useMemo(
    () =>
      (tickets ?? []).filter(
        (t) => (t?.status ?? "")?.toLowerCase() === "resolved",
      ),
    [tickets],
  );

  const reasonOptions = useMemo(
    () => toSelectList(SUPPORT_REASONS_BY_CATEGORY[form.category] ?? []),
    [form.category],
  );

  const onChangeForm = (name, value) => {
    setForm((prev) => {
      const next = { ...prev, [name]: value };
      if (name === "category") {
        next.reason = "";
      }
      return next;
    });
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const resetForm = () => {
    setForm(initForm);
    setErrors(initErrors);
  };

  const handleRemoveAttachment = (index) => {
    setForm((prev) => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index),
    }));
    setErrors((prev) => ({ ...prev, file: "" }));
  };

  const onUploadAttachment = (e) => {
    const fileList = e?.target?.files;
    if (!fileList?.length) return;

    const selectedFiles = Array.from(fileList);
    const invalidFile = selectedFiles.find(
      (file) => !file.type?.startsWith("image/"),
    );
    if (invalidFile) {
      setErrors((prev) => ({
        ...prev,
        file: "Only image files are allowed",
      }));
      dispatch(
        showSnackBar({
          message: "Only image files are allowed",
          type: "error",
        }),
      );
      e.target.value = "";
      return;
    }

    const readFiles = selectedFiles.map(
      (file) =>
        new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            resolve({
              file,
              previewUrl: reader.result,
              fileName: file.name,
            });
          };
          reader.onerror = () => reject(reader.error);
          reader.readAsDataURL(file);
        }),
    );

    Promise.all(readFiles)
      .then((newAttachments) => {
        setForm((prev) => ({
          ...prev,
          attachments: [...(prev.attachments ?? []), ...newAttachments],
        }));
        setErrors((prev) => ({ ...prev, file: "" }));
      })
      .catch(() => {
        dispatch(
          showSnackBar({
            message: "Unable to read selected images",
            type: "error",
          }),
        );
      })
      .finally(() => {
        e.target.value = "";
      });
  };

  const validate = () => {
    const next = { ...initErrors };
    if (!form?.category) next.category = "This field is required";
    if (!form?.reason) next.reason = "This field is required";
    if (!form?.problemType) next.problemType = "This field is required";
    if (!form?.description?.trim()) {
      next.description = "This field is required";
    }
    setErrors(next);
    return (
      !next.category && !next.reason && !next.problemType && !next.description
    );
  };

  const handleCreate = async () => {
    if (!validate()) return;

    const formData = new FormData();
    formData.append("description", form.description.trim());
    formData.append("mainCategory", form.category);
    formData.append("reason", form.reason);
    formData.append("problem", form.problemType);
    formData.append(
      "ticketName",
      `Ticket_${moment().format("YYYYMMDDHHmmss")}`,
    );

    (form.attachments ?? []).forEach((item) => {
      if (item?.file) {
        formData.append("attachment", item.file);
      }
    });

    const res = await dispatch(createSupportTicket(formData));
    if (res?.payload) {
      resetForm();
      setView("list");
      fetchTickets();
    }
  };

  const renderTicketSection = (label, list, emptyText) => (
    <div className="support-section">
      <div className="support-list-label">{label}</div>
      {list.length === 0 ? (
        <div className="support-empty">{emptyText}</div>
      ) : (
        list.map((ticket) => (
          <SupportTicketCard
            key={ticket?.ticketId || ticket?.id}
            ticket={ticket}
            onView={setViewTicket}
          />
        ))
      )}
    </div>
  );

  return (
    <div className="support-page com-mar mt10">
      {viewTicket ? (
        <SupportTicketDetailView
          ticket={viewTicket}
          onBack={() => setViewTicket(null)}
          onReplySuccess={handleReplySuccess}
          onCloseSuccess={handleCloseSuccess}
        />
      ) : view === "list" ? (
        <>
          <div className="support-page-header">
            <Typography className="font-bold fs22 black">Support</Typography>
            {user?.id !== 1 && (
              <div>
                <CustomButton
                  text="Create Ticket"
                  smallBtn
                  onClick={() => setView("create")}
                />
              </div>
            )}
          </div>

          <div className="support-page-card scroll-80vh">
            {renderTicketSection(
              "ACTIVE",
              activeTickets,
              "No active support tickets",
            )}
            {renderTicketSection(
              "CLOSED",
              resolvedTickets,
              "No closed support tickets",
            )}
          </div>
        </>
      ) : (
        <>
          <div className="support-page-header">
            <Typography className="font-bold fs22 black">
              Create Support
            </Typography>
            <div className="flex-row">
              <div className="mr10">
                <CustomButton
                  text="Cancel"
                  smallBtn
                  onClick={() => {
                    setView("list");
                    resetForm();
                  }}
                  redBtn
                />
              </div>
              <CustomButton text="Submit" smallBtn onClick={handleCreate} />
            </div>
          </div>

          <div className="support-page-card scroll-80vh">
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography className="support-field-label">
                  CATEGORY
                </Typography>
                <CustomSelect
                  select
                  list={toSelectList(SUPPORT_CATEGORIES)}
                  value={form.category}
                  handleChange={(e) =>
                    onChangeForm("category", e?.target?.value)
                  }
                  error={!!errors.category}
                  helperText={errors.category}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography className="support-field-label">REASON</Typography>
                <CustomSelect
                  select
                  list={reasonOptions}
                  value={form.reason}
                  handleChange={(e) => onChangeForm("reason", e?.target?.value)}
                  disabled={!form.category}
                  error={!!errors.reason}
                  helperText={errors.reason}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography className="support-field-label">
                  PROBLEM TYPE
                </Typography>
                <CustomSelect
                  select
                  list={toSelectList(SUPPORT_PROBLEM_TYPES)}
                  value={form.problemType}
                  handleChange={(e) =>
                    onChangeForm("problemType", e?.target?.value)
                  }
                  disabled={!form.reason}
                  error={!!errors.problemType}
                  helperText={errors.problemType}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography className="support-field-label">
                  DESCRIPTION
                </Typography>
                <CustomTextField
                  fullWidth
                  multiline
                  rows={5}
                  placeholder="Description"
                  value={form.description}
                  handleChange={(e) =>
                    onChangeForm("description", e?.target?.value)
                  }
                  error={!!errors.description}
                  helperText={errors.description}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography className="support-field-label">
                  ATTACHMENT (IMAGES ONLY)
                </Typography>
                {form.attachments?.length > 0 ? (
                  <FilesWithCloseButton
                    files={form.attachments}
                    onClickFile={handleRemoveAttachment}
                  />
                ) : null}
                <CustomUpload
                  onUploadFile={onUploadAttachment}
                  multipleNew
                  multiFiles
                  fileAccept="image/*"
                />
                {errors.file ? (
                  <Typography className="fs12 red-color mt5">
                    {errors.file}
                  </Typography>
                ) : null}
              </Grid>
            </Grid>
          </div>
        </>
      )}
    </div>
  );
};

export default Support;
